<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="syncingFaq"
          @click="handleSyncFaq"
        >
          <i class="ri-refresh-line"></i>
          {{ syncingFaq ? '同步中...' : '同步 FAQ' }}
        </button>
        <router-link :to="{ name: 'ChatMateKnowledgeUpload' }" class="btn btn-primary">
          <i class="ri-add-line"></i>
          上傳知識庫
        </router-link>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋標題..."
          @input="handleSearch"
        />
      </div>
      <div class="filter-group">
        <select v-model="filterTrainingStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部訓練狀態</option>
          <option value="pending">待處理</option>
          <option value="processing">處理中</option>
          <option value="completed">已完成</option>
          <option value="failed">失敗</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchKnowledgeList">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>標題</th>
            <th>檔案類型</th>
            <th>檔案大小</th>
            <th>分塊數量</th>
            <th>訓練狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="knowledgeList.length === 0">
            <td colspan="7" class="empty-state">
              沒有找到知識庫檔案
            </td>
          </tr>
          <tr v-for="knowledge in knowledgeList" :key="knowledge.id">
            <td>{{ knowledge.title }}</td>
            <td>
              <span class="badge badge-info">{{ getContentTypeText(knowledge.content_type) }}</span>
            </td>
            <td>{{ formatFileSize(knowledge.file_size) }}</td>
            <td>{{ knowledge.chunk_count || 0 }}</td>
            <td>
              <span :class="getTrainingStatusClass(knowledge.training_status)">
                {{ getTrainingStatusText(knowledge.training_status) }}
              </span>
            </td>
            <td>{{ formatDate(knowledge.created_at) }}</td>
            <td>
              <div class="action-buttons">
                <router-link
                  :to="{ name: 'ChatMateKnowledgeDetail', params: { id: knowledge.id } }"
                  class="btn btn-sm btn-secondary"
                >
                  <i class="ri-eye-line"></i>
                  查看
                </router-link>
                <button
                  v-if="knowledge.training_status === 'completed'"
                  class="btn btn-sm btn-secondary"
                  @click="handleDownload(knowledge.id)"
                  :disabled="downloadingIds.has(knowledge.id)"
                  title="下載原始檔案"
                >
                  <i class="ri-download-line"></i>
                  <span v-if="downloadingIds.has(knowledge.id)">取得中...</span>
                  <span v-else>下載</span>
                </button>
                <button 
                  v-if="knowledge.training_status === 'failed' || knowledge.training_status === 'pending'"
                  class="btn btn-sm btn-primary" 
                  @click="handleTrain(knowledge.id)"
                  :disabled="trainingIds.has(knowledge.id)"
                >
                  <i class="ri-refresh-line"></i>
                  <span v-if="trainingIds.has(knowledge.id)">訓練中...</span>
                  <span v-else>重新訓練</span>
                </button>
                <button 
                  class="btn btn-sm btn-danger" 
                  @click="handleDelete(knowledge.id)"
                >
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分頁 -->
      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="currentPage"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="goToPage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, watch } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { chatmateService } from '@/api/modules/chatmate.js';
import { showError, showDeleteConfirm, showSuccess } from '@/utils/swal.js';

export default {
  name: 'ChatMateKnowledgeList',
  setup() {
    const { pageTitle } = usePageTitle('知識庫管理');
    const knowledgeList = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterTrainingStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const trainingIds = ref(new Set());
    const downloadingIds = ref(new Set());
    const syncingFaq = ref(false);
    const pagination = reactive({
      totalItems: 0,
      totalPages: 1
    });

    const fetchKnowledgeList = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        // 只有當 trainingStatus 不是空字串時才加入參數
        if (filterTrainingStatus.value && filterTrainingStatus.value.trim() !== '') {
          params.trainingStatus = filterTrainingStatus.value;
        }

        const response = await chatmateService.getKnowledgeList(params);

        if (response && response.success && response.data) {
          knowledgeList.value = response.data.items || [];
          pagination.totalItems = response.data.pagination?.totalItems || 0;
          pagination.totalPages = response.data.pagination?.totalPages || 1;
          
          // 檢查是否需要啟動輪詢
          checkAndStartPolling();
        } else {
          error.value = response?.message || '獲取知識庫列表失敗';
        }
      } catch (err) {
        console.error('獲取知識庫列表失敗:', err);
        error.value = err.response?.data?.message || err.message || '獲取知識庫列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchKnowledgeList();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchKnowledgeList();
    };

    const handleSyncFaq = async () => {
      if (syncingFaq.value) return;
      syncingFaq.value = true;
      try {
        const response = await chatmateService.syncFaqToKnowledge();
        if (response && response.success && response.data) {
          const { total_items: totalItems, message } = response.data;
          await showSuccess(
            '同步已啟動',
            totalItems === 0
              ? '目前沒有啟用中的 FAQ，已清除舊的 FAQ 知識庫紀錄。'
              : `已同步 ${totalItems} 筆 FAQ 至知識庫，訓練將在背景執行，請稍後重新整理查看。`
          );
          fetchKnowledgeList();
        } else {
          await showError('同步失敗', response?.data?.message || response?.message || '同步失敗');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || '同步失敗';
        await showError('同步失敗', msg);
      } finally {
        syncingFaq.value = false;
      }
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= pagination.totalPages) {
        currentPage.value = page;
        fetchKnowledgeList();
      }
    };

    const handleTrain = async (knowledgeId) => {
      // 避免重複點擊
      if (trainingIds.value.has(knowledgeId)) {
        return;
      }

      trainingIds.value.add(knowledgeId);

      try {
        const response = await chatmateService.trainKnowledge(knowledgeId);

        if (response && response.success) {
          await showSuccess('訓練已開始', '知識庫訓練已開始，請稍後查看訓練狀態');
          // 更新該項目的訓練狀態
          const knowledge = knowledgeList.value.find(k => k.id === knowledgeId);
          if (knowledge) {
            knowledge.training_status = 'processing';
          }
          // 2 秒後刷新列表以獲取最新狀態
          setTimeout(() => {
            fetchKnowledgeList();
          }, 2000);
        } else {
          await showError('訓練失敗', response?.message || '觸發訓練失敗');
        }
      } catch (err) {
        console.error('觸發訓練失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '觸發訓練失敗';
        await showError('訓練失敗', errorMessage);
      } finally {
        trainingIds.value.delete(knowledgeId);
      }
    };

    const handleDownload = async (knowledgeId) => {
      if (downloadingIds.value.has(knowledgeId)) return;
      downloadingIds.value.add(knowledgeId);
      try {
        const response = await chatmateService.getKnowledgeDownloadUrl(knowledgeId);
        if (response && response.success && response.data?.url) {
          const a = document.createElement('a');
          a.href = response.data.url;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          await showError('下載失敗', response?.message || '無法取得下載連結');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || '下載失敗';
        await showError('下載失敗', msg);
      } finally {
        downloadingIds.value.delete(knowledgeId);
      }
    };

    const handleDelete = async (knowledgeId) => {
      const knowledge = knowledgeList.value.find(k => k.id === knowledgeId);
      const title = knowledge ? knowledge.title : '此知識庫';

      try {
        const confirmed = await showDeleteConfirm(title);
        if (!confirmed) return;

        const response = await chatmateService.deleteKnowledge(knowledgeId);

        if (response && response.success) {
          await showSuccess('刪除成功', '知識庫已刪除');
          await fetchKnowledgeList();
        } else {
          await showError('刪除失敗', response?.message || '刪除知識庫失敗');
        }
      } catch (err) {
        console.error('刪除知識庫失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '刪除知識庫失敗';
        await showError('刪除失敗', errorMessage);
      }
    };

    const getContentTypeText = (type) => {
      const typeMap = {
        text: '文字',
        faq: 'FAQ',
        markdown: 'Markdown',
        pdf: 'PDF',
        docx: 'DOCX',
        xlsx: 'Excel',
        json: 'JSON',
        url: 'URL'
      };
      return typeMap[type] || type;
    };

    const getTrainingStatusText = (status) => {
      const statusMap = {
        pending: '待處理',
        processing: '處理中',
        completed: '已完成',
        failed: '失敗'
      };
      return statusMap[status] || status || '未知';
    };

    const getTrainingStatusClass = (status) => {
      const classMap = {
        pending: 'badge badge-secondary',
        processing: 'badge badge-warning',
        completed: 'badge badge-success',
        failed: 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatFileSize = (bytes) => {
      if (!bytes) return '-';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    // 自動輪詢機制：如果有項目正在處理中，定期刷新列表
    let pollingInterval = null;
    
    const stopPolling = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
    };
    
    const startPolling = () => {
      // 如果已經有輪詢在執行，不重複啟動
      if (pollingInterval) {
        return;
      }
      
      // 每 5 秒檢查一次是否有項目正在處理中
      pollingInterval = setInterval(() => {
        const hasProcessing = knowledgeList.value.some(
          k => k.training_status === 'processing'
        );
        
        if (hasProcessing) {
          // 靜默刷新（不顯示 loading）
          // 使用單獨的 fetch 避免觸發 loading 狀態
          const params = {
            page: currentPage.value,
            pageSize: pageSize.value
          };
          
          if (searchQuery.value) {
            params.search = searchQuery.value;
          }
          
          // 只有當 trainingStatus 不是空字串時才加入參數
          if (filterTrainingStatus.value && filterTrainingStatus.value.trim() !== '') {
            params.trainingStatus = filterTrainingStatus.value;
          }
          
          chatmateService.getKnowledgeList(params).then(response => {
            if (response && response.success && response.data) {
              knowledgeList.value = response.data.items || [];
              pagination.totalItems = response.data.pagination?.totalItems || 0;
              pagination.totalPages = response.data.pagination?.totalPages || 1;
              
              // 如果沒有處理中的項目了，停止輪詢
              const stillProcessing = knowledgeList.value.some(
                k => k.training_status === 'processing'
              );
              if (!stillProcessing) {
                stopPolling();
              }
            }
          }).catch(err => {
            console.error('輪詢刷新失敗:', err);
          });
        } else {
          // 沒有處理中的項目，停止輪詢
          stopPolling();
        }
      }, 5000); // 每 5 秒輪詢一次
    };
    
    // 檢查是否需要啟動輪詢
    const checkAndStartPolling = () => {
      const hasProcessing = knowledgeList.value.some(
        k => k.training_status === 'processing'
      );
      
      if (hasProcessing) {
        startPolling();
      } else {
        stopPolling();
      }
    };
    
    onMounted(() => {
      fetchKnowledgeList().then(() => {
        // 載入完成後檢查是否需要啟動輪詢
        checkAndStartPolling();
      });
    });
    
    // 組件卸載時清理輪詢
    onUnmounted(() => {
      stopPolling();
    });

    return {
      pageTitle,
      knowledgeList,
      loading,
      error,
      searchQuery,
      filterTrainingStatus,
      currentPage,
      pagination,
      trainingIds,
      downloadingIds,
      syncingFaq,
      fetchKnowledgeList,
      handleSearch,
      handleFilterChange,
      handleSyncFaq,
      goToPage,
      handleDownload,
      handleTrain,
      handleDelete,
      getContentTypeText,
      getTrainingStatusText,
      getTrainingStatusClass,
      formatFileSize,
      formatDate
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper {
  background: white;
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: visible;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  -webkit-overflow-scrolling: touch;
}

table {
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
}

thead {
  background-color: #f5f5f5;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  font-weight: 600;
  color: #333;
}

tbody tr:hover {
  background-color: #f9f9f9;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 40px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;
}
</style>
